// Domain · CompanyCard
//
// The canonical "company as a party block" — how a company is printed when it
// appears as a party on a document: role eyebrow, linked name, postal address,
// then the statutory identifiers. No panel, no tint: on an invoice the parties
// are printed text, not UI chrome. Defined ONCE and referenced everywhere:
// companies' own view picks `companySchema.card`; other entities lift it
// across their relation via `liftField(companySchema.card, { via })`.
//
// `extras` keeps its labelled KV treatment for consumer-specific context
// (per-side address, commercial party, sales employee).

import React from "react";
import { useTranslation } from "react-i18next";
import type { ToneName } from "@ds/tokens/palette";
import type { CompanyJoin } from "@shared/types/company";
import { Box } from "@ds/layout/Box";
import { Stack } from "@ds/layout/Stack";
import { Inline } from "@ds/layout/Inline";
import { Eyebrow } from "@ds/display/Eyebrow";
import { TextValue } from "@ds/display/TextValue";
import { KVGrid, type KVItem } from "@ds/layout/KVGrid";
import { CompanyChip } from "@ds/domain/CompanyChip";
import { CategoryRow } from "@ds/domain/CategoryChip";

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface CompanyCardInput {
  /** The company to render. `null`/`undefined` → em-dash title. */
  company?: CompanyJoin | null;
  /**
   * Eyebrow label — the company's ROLE in this context (e.g.
   * "Organizacja odzysku"). i18n key. Optional; omit for a bare card.
   */
  eyebrow?: string;
  /** @deprecated No longer tints — the block has no fill. Accepted so
   *  existing call sites keep compiling; remove at the next sweep. */
  tone?: ToneName;
  /**
   * Eyebrow override as an already-resolved string (not an i18n key). Use when
   * the label is composed at the call site (e.g. "Dostawca · Załadunek").
   * Takes precedence over `eyebrow`.
   */
  eyebrowText?: string;
  /**
   * Extra KV rows appended after the company identifiers (NIP / BDO / city) —
   * context the CARD doesn't know about (e.g. a transaction's per-side address,
   * commercial party, exporter/importer, sales employee). Shares the same
   * KVGrid so it reads as one block.
   */
  extras?: KVItem[];
  /** How many columns the KV block uses. Default 1 — one extension per row,
   *  each its own paragraph. */
  extrasColumns?: 1 | 2;
  /**
   * `"boxed"` (default) draws the thin frame — the card is a distinct object
   * on a page of other objects. `"plain"` drops it: on a document sheet the
   * parties are printed text, and a frame there is a box inside a box.
   */
  variant?: "boxed" | "plain";
  /** Header right slot (e.g. status pills). */
  right?: React.ReactNode;
  /** Rich content below the KV block (e.g. invoice bank accounts). */
  footer?: React.ReactNode;
  testId?: string;
}

export type CompanyCardProps = CompanyCardInput;

export function CompanyCard({
  company,
  eyebrow,
  eyebrowText,
  extras,
  extrasColumns = 1,
  variant = "boxed",
  right,
  footer,
  testId,
}: CompanyCardProps) {
  const { t } = useTranslation();

  const eyebrowNode = eyebrowText ?? (eyebrow ? t(eyebrow) : undefined);
  // One address line, the way it is written on an envelope:
  // "street + number, postcode city".
  const addressLine = [
    company?.street,
    [company?.zip_code, company?.city].filter(Boolean).join(" "),
  ].filter((part) => part && part.trim().length > 0).join(", ");

  return (
    <Box
      border={variant === "boxed"}
      shape={variant === "boxed" ? "rounded-lg" : "rectangle"}
      size={variant === "boxed" ? "md" : "none"}
      testId={testId}
    >
      {/* Identity lines sit tight together; the footer (the consumer's own
          block — a bank account, say) is a separate paragraph and gets air. */}
      <Stack gap="lg">
        <Stack gap="xs">
          {(eyebrowNode || right) && (
            <Inline justify="between" align="center">
              {eyebrowNode ? <Eyebrow>{eyebrowNode}</Eyebrow> : <span />}
              {right}
            </Inline>
          )}

          {company ? (
            <CompanyChip company={company} variant="inline" />
          ) : (
            <TextValue as="div" tone="muted">—</TextValue>
          )}

          {/* Postal identity: one address line, then the statutory identifiers. */}
          {addressLine && (
            <TextValue as="div" size="sm" tone="secondary">{addressLine}</TextValue>
          )}
          {company?.nip && (
            <TextValue as="div" size="sm" tone="secondary">
              {t("companies.fields.nip", "NIP")} {company.nip}
            </TextValue>
          )}
          {company?.bdo && (
            <TextValue as="div" size="sm" tone="secondary">
              {t("companies.fields.bdo", "BDO")} {company.bdo}
            </TextValue>
          )}

          {company?.categories && company.categories.length > 0 && (
            <CategoryRow categories={company.categories} size="sm" />
          )}

        </Stack>

        {/* Consumer-specific context keeps its labelled treatment — and each
            extension reads as its own paragraph, not another identity line. */}
        {extras && extras.length > 0 && (
          <KVGrid items={extras} layout="grid" columns={extrasColumns} />
        )}
        {footer}
      </Stack>
    </Box>
  );
}
